import type { IScheduledQuotesBlotterItem } from 'apiConnector/scheduledRun/scheduledQuotesBlotter';
import type {
  ColumnGroupPreferenceState,
  ColumnPreferenceState,
  IBlotterPreferenceState,
} from 'components/shared/Blotter/Grid/blotter.model';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import type {
  IScheduledQuotesBlotterFilters,
  SchedulePanelTab,
} from 'state/blotters/scheduledQuotes/scheduledQuotesBlotter.model';

export type ScheduledQuotesBlotterAction =
  | ScheduledQuotesBlotterColumnDefinitionReceived
  | ScheduledQuotesBlotterPanelClosed
  | ScheduledQuotesBlotterSelectionChanged
  | ScheduledQuotesBlotterFilterChanged
  | ScheduledQuotesBlotterColumnStateChanged
  | ScheduledQuotesBlotterPreferenceStatePersisted
  | ScheduledQuotesBlotterItemsRequested
  | ScheduledQuotesBlotterItemsReceived
  | ScheduledQuotesBlotterItemsFetchingFailed
  | ScheduledQuotesBlotterFiltersLoaded
  | ScheduledQuotesBlotterFiltersLoadingFailed
  | ScheduledQuotesBlotterPreferenceStateGet
  | ScheduledQuotesBlotterColumnDefinitionFailed
  | ScheduledQuotesBlotterPreferenceStateLoaded
  | ScheduledQuotesBlotterPreferenceStateLoadingFailed
  | ScheduledQuotesBlotterPanelTabChanged
  | ScheduledQuotesBlotterItemsReloaded
  | ScheduledQuotesBlotterItemReceived;

//////////////////////////////////////////////////
export function scheduledQuotesBlotterColumnDefinitionReceived(
  columnDefinition: readonly IBlotterColumnDefinition<IScheduledQuotesBlotterItem>[],
) {
  return {
    type: 'SCHEDULED_QUOTES_BLOTTER_COLUMN_DEFINITION_RECEIVED' as const,
    columnDefinition,
  };
}
export type ScheduledQuotesBlotterColumnDefinitionReceived = ReturnType<
  typeof scheduledQuotesBlotterColumnDefinitionReceived
>;

//////////////////////////////////////////////////
export function scheduledQuotesBlotterPanelClosed() {
  return {
    type: 'SCHEDULED_QUOTES_BLOTTER_PANEL_CLOSED' as const,
  };
}
export type ScheduledQuotesBlotterPanelClosed = ReturnType<typeof scheduledQuotesBlotterPanelClosed>;

///////////////////////////////////////////////////
export function scheduledQuotesBlotterSelectionChanged(selectedRowId: string | undefined) {
  return {
    type: 'SCHEDULED_QUOTES_BLOTTER_SELECTION_CHANGED' as const,
    selectedRowId,
  };
}
export type ScheduledQuotesBlotterSelectionChanged = ReturnType<typeof scheduledQuotesBlotterSelectionChanged>;

//////////////////////////////////////////////////////////////////
type Filter = keyof IScheduledQuotesBlotterFilters;
interface IScheduledQuotesBlotterFilterChanged<F extends Filter> {
  type: 'SCHEDULED_QUOTES_BLOTTER_FILTER_CHANGED';
  filter: F;
  value: IScheduledQuotesBlotterFilters[F];
}
export type ScheduledQuotesBlotterFilterChanged = IScheduledQuotesBlotterFilterChanged<Filter>;

export function scheduledQuotesBlotterFilterChanged<F extends Filter>(
  filter: F,
  value: IScheduledQuotesBlotterFilters[F],
): ScheduledQuotesBlotterFilterChanged {
  return {
    type: 'SCHEDULED_QUOTES_BLOTTER_FILTER_CHANGED' as const,
    filter,
    value,
  };
}

//////////////////////////////////////////////////

export function scheduledQuotesBlotterColumnStateChanged(
  columnState: readonly ColumnPreferenceState[],
  columnGroupState?: readonly ColumnGroupPreferenceState[],
) {
  return { type: 'SCHEDULED_QUOTES_BLOTTER_COLUMN_STATE_CHANGED' as const, columnState, columnGroupState };
}
export type ScheduledQuotesBlotterColumnStateChanged = ReturnType<typeof scheduledQuotesBlotterColumnStateChanged>;

//////////////////////////////////////////////////
export function scheduledQuotesBlotterPreferenceStatePersisted(blotterPreferenceState: IBlotterPreferenceState) {
  return { type: 'SCHEDULED_QUOTES_BLOTTER_PREFERENCE_STATE_PERSISTED' as const, blotterPreferenceState };
}
export type ScheduledQuotesBlotterPreferenceStatePersisted = ReturnType<
  typeof scheduledQuotesBlotterPreferenceStatePersisted
>;

//////////////////////////////////////////////////
export function scheduledQuotesBlotterItemsRequested() {
  return { type: 'SCHEDULED_QUOTES_BLOTTER_ITEMS_REQUESTED' as const };
}
export type ScheduledQuotesBlotterItemsRequested = ReturnType<typeof scheduledQuotesBlotterItemsRequested>;

//////////////////////////////////////////////////
export function scheduledQuotesBlotterItemsReceived(
  items: readonly IScheduledQuotesBlotterItem[],
  totalItems: number,
  totalPages: number,
) {
  return {
    type: 'SCHEDULED_QUOTES_BLOTTER_ITEMS_RECEIVED' as const,
    items,
    totalItems,
    totalPages,
  };
}
export type ScheduledQuotesBlotterItemsReceived = ReturnType<typeof scheduledQuotesBlotterItemsReceived>;

////////////////////////////////////////////////
export function scheduledQuotesBlotterItemsFetchingFailed(error: string) {
  return { type: 'SCHEDULED_QUOTES_BLOTTER_ITEMS_FETCHING_FAILED' as const, error };
}
export type ScheduledQuotesBlotterItemsFetchingFailed = ReturnType<typeof scheduledQuotesBlotterItemsFetchingFailed>;

//////////////////////////////////////////////////
export function scheduledQuotesBlotterFiltersLoaded(filters: IScheduledQuotesBlotterFilters) {
  return {
    type: 'SCHEDULED_QUOTES_BLOTTER_FILTERS_LOADED' as const,
    filters,
  };
}
export type ScheduledQuotesBlotterFiltersLoaded = ReturnType<typeof scheduledQuotesBlotterFiltersLoaded>;

//////////////////////////////////////////////////
export function scheduledQuotesBlotterFiltersLoadingFailed() {
  return {
    type: 'SCHEDULED_QUOTES_BLOTTER_FILTERS_LOADING_FAILED' as const,
  };
}
export type ScheduledQuotesBlotterFiltersLoadingFailed = ReturnType<typeof scheduledQuotesBlotterFiltersLoadingFailed>;

//////////////////////////////////////////////////
export function scheduledQuotesBlotterPreferenceStateGet() {
  return { type: 'SCHEDULED_QUOTES_BLOTTER_PREFERENCE_STATE_GET' as const };
}

export type ScheduledQuotesBlotterPreferenceStateGet = ReturnType<typeof scheduledQuotesBlotterPreferenceStateGet>;

//////////////////////////////////////////////////
export function scheduledQuotesBlotterColumnDefinitionFailed() {
  return { type: 'SCHEDULED_QUOTES_BLOTTER_COLUMN_DEFINITION_FAILED' as const };
}
export type ScheduledQuotesBlotterColumnDefinitionFailed = ReturnType<
  typeof scheduledQuotesBlotterColumnDefinitionFailed
>;

//////////////////////////////////////////////////
export function scheduledQuotesBlotterPreferenceStateLoaded(blotterPreferenceState: IBlotterPreferenceState) {
  return { type: 'SCHEDULED_QUOTES_BLOTTER_PREFERENCE_STATE_LOADED' as const, blotterPreferenceState };
}
export type ScheduledQuotesBlotterPreferenceStateLoaded = ReturnType<
  typeof scheduledQuotesBlotterPreferenceStateLoaded
>;

export function scheduledQuotesBlotterPreferenceStateLoadingFailed() {
  return {
    type: 'SCHEDULED_QUOTES_BLOTTER_PREFERENCE_STATE_LOADING_FAILED' as const,
  };
}
export type ScheduledQuotesBlotterPreferenceStateLoadingFailed = ReturnType<
  typeof scheduledQuotesBlotterPreferenceStateLoadingFailed
>;

export function scheduledQuotesBlotterPanelTabChanged(tab: SchedulePanelTab) {
  return {
    type: 'SCHEDULED_QUOTES_BLOTTER_PANEL_TAB_CHANGED' as const,
    tab,
  };
}
export type ScheduledQuotesBlotterPanelTabChanged = ReturnType<typeof scheduledQuotesBlotterPanelTabChanged>;

export function scheduledQuotesBlotterItemsReloaded() {
  return { type: 'SCHEDULED_QUOTES_BLOTTER_ITEMS_RELOADED' as const };
}
export type ScheduledQuotesBlotterItemsReloaded = ReturnType<typeof scheduledQuotesBlotterItemsReloaded>;

export function scheduledQuotesBlotterItemReceived(item: IScheduledQuotesBlotterItem) {
  return { type: 'SCHEDULED_QUOTES_BLOTTER_ITEM_RECEIVED' as const, item };
}
export type ScheduledQuotesBlotterItemReceived = ReturnType<typeof scheduledQuotesBlotterItemReceived>;
